import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Alert, 
  Chip,
  Box,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Divider,
  Grid,
} from '@mui/material';
import {
  Key as KeyIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { apiKeyService } from '../services/api';

const ApiKeysPage = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedKeyForMenu, setSelectedKeyForMenu] = useState(null);
  const [newKeyData, setNewKeyData] = useState({
    keyName: '',
    permissions: {
      agents: ['read'],
      calls: ['read'],
      resources: ['read'],
      keys: ['read']
    },
    rateLimitPerMinute: 60,
    rateLimitPerHour: 1000
  });

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const validateForm = () => {
    const errors = {};
    
    if (!newKeyData.keyName || newKeyData.keyName.trim().length < 2) {
      errors.keyName = 'API key name must be at least 2 characters long';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const fetchApiKeys = async () => {
    try {
      const response = await apiKeyService.getApiKeys();
      setApiKeys(response || []);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching API keys:', err);
      setError('Failed to fetch API keys.');
      setLoading(false);
    }
  };

  const handleCreateKey = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      // Convert permissions object to array format expected by backend
      const permissionsArray = [];
      if (newKeyData.permissions.agents?.includes('read')) permissionsArray.push('agent:read');
      if (newKeyData.permissions.agents?.includes('create')) permissionsArray.push('agent:write');
      if (newKeyData.permissions.calls?.includes('read')) permissionsArray.push('call:read');
      if (newKeyData.permissions.calls?.includes('create')) permissionsArray.push('call:write');
      if (newKeyData.permissions.resources?.includes('read')) permissionsArray.push('read');
      if (newKeyData.permissions.keys?.includes('read')) permissionsArray.push('read');
      
      // Default to read permission if none selected
      if (permissionsArray.length === 0) {
        permissionsArray.push('read');
      }
      
      const requestData = {
        name: newKeyData.keyName.trim(),
        permissions: permissionsArray,
        rateLimit: {
          requests: newKeyData.rateLimitPerHour || 1000,
          window: 3600
        }
      };
      
      const response = await apiKeyService.createApiKey(requestData);
      
      const newApiKey = {
        id: response.id,
        keyName: response.name,
        apiKey: response.key,
        permissions: response.permissions,
        isActive: response.isActive,
        createdAt: response.createdAt,
        lastUsedAt: null,
        usageCount: 0,
        rateLimitPerMinute: newKeyData.rateLimitPerMinute,
        rateLimitPerHour: newKeyData.rateLimitPerHour
      };
      
      setApiKeys([...apiKeys, newApiKey]);
      setShowCreateModal(false);
      setNewKeyData({
        keyName: '',
        permissions: {
          agents: ['read'],
          calls: ['read'],
          resources: ['read'],
          keys: ['read']
        },
        rateLimitPerMinute: 60,
        rateLimitPerHour: 1000
      });
      setValidationErrors({});
      
      setSelectedKey(newApiKey);
      setShowViewModal(true);
    } catch (err) {
      console.error('Error creating API key:', err);
      setError('Failed to create API key.');
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to delete this API key?')) return;
    
    try {
      await apiKeyService.deleteApiKey(keyId);
      setApiKeys(apiKeys.filter(key => key.id !== keyId));
    } catch (err) {
      console.error('Error deleting API key:', err);
      setError('Failed to delete API key.');
    }
  };

  const handleRegenerateKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to regenerate this API key? The old key will be invalidated.')) return;
    
    try {
      const response = await apiKeyService.regenerateApiKey(keyId);
      
      setApiKeys(apiKeys.map(key => 
        key.id === keyId ? { 
          ...key, 
          apiKey: response.apiKey,
          lastUsedAt: null,
          usageCount: 0
        } : key
      ));
      
      setSelectedKey(response);
      setShowViewModal(true);
    } catch (err) {
      console.error('Error regenerating API key:', err);
      setError('Failed to regenerate API key.');
    }
  };

  const handleToggleStatus = async (keyId, currentStatus) => {
    try {
      await apiKeyService.updateApiKey(keyId, {
        isActive: !currentStatus
      });
      
      setApiKeys(apiKeys.map(key => 
        key.id === keyId ? { ...key, isActive: !currentStatus } : key
      ));
    } catch (err) {
      console.error('Error updating API key status:', err);
      setError('Failed to update API key status.');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const formatPermissions = (permissions) => {
    if (!permissions || Object.keys(permissions).length === 0) {
      return <Chip label="Full Access" color="success" size="small" />;
    }
    
    return Object.entries(permissions).map(([resource, actions]) => (
      <Chip 
        key={resource} 
        label={`${resource}: ${Array.isArray(actions) ? actions.join(', ') : actions}`}
        color="primary" 
        size="small" 
        sx={{ mr: 0.5, mb: 0.5 }}
      />
    ));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const handleMenuOpen = (event, key) => {
    setAnchorEl(event.currentTarget);
    setSelectedKeyForMenu(key);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedKeyForMenu(null);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            API Keys
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your API keys for accessing the Call Ninja API
          </Typography>
        </Box>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => setShowCreateModal(true)}
        >
          Create API Key
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {apiKeys.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <KeyIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No API Keys Found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create your first API key to start using the Call Ninja API
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => setShowCreateModal(true)}
            >
              Create Your First API Key
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Permissions</strong></TableCell>
                  <TableCell><strong>Rate Limits</strong></TableCell>
                  <TableCell><strong>Last Used</strong></TableCell>
                  <TableCell><strong>Usage Count</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {apiKeys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {key.keyName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {key.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {formatPermissions(key.permissions)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {key.rateLimitPerMinute}/min
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {key.rateLimitPerHour}/hour
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {key.lastUsedAt ? formatDate(key.lastUsedAt) : 'Never'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {key.usageCount || 0}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={key.isActive ? 'Active' : 'Inactive'}
                        color={key.isActive ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => handleMenuOpen(e, key)}
                        size="small"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Create API Key Dialog */}
      <Dialog open={showCreateModal} onClose={() => setShowCreateModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New API Key</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Key Name"
                placeholder="Enter a name for your API key"
                value={newKeyData.keyName}
                onChange={(e) => setNewKeyData({...newKeyData, keyName: e.target.value})}
                error={!!validationErrors.keyName}
                helperText={validationErrors.keyName}
              />
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Rate Limit (per minute)"
                type="number"
                value={newKeyData.rateLimitPerMinute}
                onChange={(e) => setNewKeyData({...newKeyData, rateLimitPerMinute: parseInt(e.target.value)})}
                helperText="Requests per minute"
              />
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Rate Limit (per hour)"
                type="number"
                value={newKeyData.rateLimitPerHour}
                onChange={(e) => setNewKeyData({...newKeyData, rateLimitPerHour: parseInt(e.target.value)})}
                helperText="Requests per hour"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Permissions
              </Typography>
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Agents
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newKeyData.permissions.agents?.includes('read')}
                          onChange={(e) => {
                            const agents = e.target.checked 
                              ? [...(newKeyData.permissions.agents || []), 'read']
                              : (newKeyData.permissions.agents || []).filter(p => p !== 'read');
                            setNewKeyData({
                              ...newKeyData,
                              permissions: { ...newKeyData.permissions, agents }
                            });
                          }}
                        />
                      }
                      label="Read"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newKeyData.permissions.agents?.includes('create')}
                          onChange={(e) => {
                            const agents = e.target.checked 
                              ? [...(newKeyData.permissions.agents || []), 'create']
                              : (newKeyData.permissions.agents || []).filter(p => p !== 'create');
                            setNewKeyData({
                              ...newKeyData,
                              permissions: { ...newKeyData.permissions, agents }
                            });
                          }}
                        />
                      }
                      label="Create"
                    />
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Calls
                    </Typography>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newKeyData.permissions.calls?.includes('read')}
                          onChange={(e) => {
                            const calls = e.target.checked 
                              ? [...(newKeyData.permissions.calls || []), 'read']
                              : (newKeyData.permissions.calls || []).filter(p => p !== 'read');
                            setNewKeyData({
                              ...newKeyData,
                              permissions: { ...newKeyData.permissions, calls }
                            });
                          }}
                        />
                      }
                      label="Read"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newKeyData.permissions.calls?.includes('create')}
                          onChange={(e) => {
                            const calls = e.target.checked 
                              ? [...(newKeyData.permissions.calls || []), 'create']
                              : (newKeyData.permissions.calls || []).filter(p => p !== 'create');
                            setNewKeyData({
                              ...newKeyData,
                              permissions: { ...newKeyData.permissions, calls }
                            });
                          }}
                        />
                      }
                      label="Create"
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreateModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreateKey} variant="contained">
            Create API Key
          </Button>
        </DialogActions>
      </Dialog>

      {/* View API Key Dialog */}
      <Dialog open={showViewModal} onClose={() => setShowViewModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>API Key Details</DialogTitle>
        <DialogContent>
          {selectedKey && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedKey.keyName}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                ID: {selectedKey.id}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle1" gutterBottom>
                API Key
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                p: 2,
                bgcolor: 'grey.100',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.875rem'
              }}>
                <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-all' }}>
                  {selectedKey.apiKey}
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={() => copyToClipboard(selectedKey.apiKey)}
                >
                  <CopyIcon />
                </IconButton>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Copy this key and store it securely. You won't be able to see it again.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowViewModal(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          setSelectedKey(selectedKeyForMenu);
          setShowViewModal(true);
          handleMenuClose();
        }}>
          <VisibilityIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={() => {
          handleRegenerateKey(selectedKeyForMenu.id);
          handleMenuClose();
        }}>
          <RefreshIcon sx={{ mr: 1 }} />
          Regenerate
        </MenuItem>
        <MenuItem onClick={() => {
          handleToggleStatus(selectedKeyForMenu.id, selectedKeyForMenu.isActive);
          handleMenuClose();
        }}>
          {selectedKeyForMenu?.isActive ? (
            <>
              <CancelIcon sx={{ mr: 1 }} />
              Deactivate
            </>
          ) : (
            <>
              <CheckCircleIcon sx={{ mr: 1 }} />
              Activate
            </>
          )}
        </MenuItem>
        <Divider />
        <MenuItem 
          onClick={() => {
            handleDeleteKey(selectedKeyForMenu.id);
            handleMenuClose();
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </Container>
  );
};

export default ApiKeysPage; 